// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import { participationSnapshotDocId } from './recordParticipationSnapshot'
import toMillis from '../dataHelpers/matrixDataHelpers/toMillis'

/**
 * Datum van een vastgelegde participatiemeting corrigeren. De datum bepaalt in
 * welk boekjaar de verandering meetelt (cohortprincipe), dus een meting die te
 * laat is geregistreerd moet naar de echte meetdag verplaatst kunnen worden.
 *
 * Verplaatsen = het document opnieuw schrijven onder de dag-docid van de
 * nieuwe datum en het oude document soft-deleten (ADR-008) — zo blijft de
 * één-meting-per-dag-invariant intact en normaliseren legacy-metingen met een
 * uuid-docid vanzelf. Bestaat er al een meting op de doeldag, dan weigeren we
 * zichtbaar in plaats van stil te overschrijven.
 */

/**
 * Lokale dagsleutel (yyyy-mm-dd) van een meting-Timestamp of Date.
 *
 * @param {*} timestamp - Firestore Timestamp, Date of epoch-ms
 * @returns {string|null}
 */
const dagVan = (timestamp) => {
  const ms = toMillis(timestamp)
  if (!Number.isFinite(ms)) return null
  const d = new Date(ms)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * Pure bouwer voor het verplaatste document. De Timestamp wordt 12:00 lokale
 * tijd op de doeldag — het uur is irrelevant, maar midden op de dag voorkomt
 * tijdzone-randgevallen rond middernacht.
 *
 * @param {object} measurement - Het bestaande ParticipationMeasurements-doc (met docid)
 * @param {Date} newDate - De gecorrigeerde meetdag
 * @returns {{ docId: string, payload: object }}
 */
const buildMovedSnapshot = (measurement, newDate) => {
  const docId = participationSnapshotDocId(measurement.PersonaID, newDate)
  const { docid, ...fields } = measurement

  return {
    docId,
    payload: {
      ...fields,
      ID: docId,
      Date: dagVan(newDate),
      Timestamp: new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 12, 0, 0),
    },
  }
}

/**
 * Heeft dezelfde persona al een (andere) meting op de doeldag?
 *
 * @param {Array<object>} measurements - Alle metingen van de tenant (of persona)
 * @param {object} measurement - De meting die verplaatst wordt
 * @param {Date} newDate - De doeldag
 * @returns {boolean}
 */
const snapshotDateConflict = (measurements, measurement, newDate) => {
  const doelDag = dagVan(newDate)

  return (Array.isArray(measurements) ? measurements : []).some((m) =>
    m?.PersonaID === measurement.PersonaID
    && m.docid !== measurement.docid
    && dagVan(m.Timestamp) === doelDag,
  )
}

/**
 * @param {object} input
 * @param {object} input.measurement - Het te verplaatsen meting-doc (met docid)
 * @param {Date} input.newDate - De gecorrigeerde meetdag
 * @param {Array<object>} input.measurements - De metingenlijst voor de conflictcheck
 * @param {string} input.deletedBy - Users-ID voor de soft delete van het oude doc
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
const moveParticipationSnapshot = async ({ measurement, newDate, measurements, deletedBy }) => {
  if (dagVan(newDate) === dagVan(measurement.Timestamp)) return { ok: true }

  if (snapshotDateConflict(measurements, measurement, newDate)) {
    return { ok: false, reason: 'Er staat al een meting op die datum. Verplaats of verwijder die eerst.' }
  }

  const { docId, payload } = buildMovedSnapshot(measurement, newDate)
  const collection = db.collection('ParticipationMeasurements')

  // Atomair: nieuw doc + soft delete van het oude in één batch, anders kan de
  // meting bij een halve fout dubbel bestaan en dubbel tellen in de teller.
  const batch = db.batch()
  batch.set(collection.doc(docId), payload)
  batch.update(collection.doc(measurement.docid), {
    Deleted: true,
    DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
    DeletedBy: deletedBy || '',
  })
  await batch.commit()

  return { ok: true }
}

export default moveParticipationSnapshot
export { buildMovedSnapshot, snapshotDateConflict, dagVan }
